/** @format */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";

/**
 * Compute Individual Payslip Component
 * Created: October 30, 2025
 * Purpose: Generate payslip for individual employee with complete calculation
 */
const ComputePayslip = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs().endOf("month"));
  const [accountDate, setAccountDate] = useState(dayjs());
  const [manualWorkDays, setManualWorkDays] = useState(null);
  const [useManualWorkDays, setUseManualWorkDays] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [payslipData, setPayslipData] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);

  // Fetch employees on mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get("/employee/getAllEmployees");
      if (response.data && Array.isArray(response.data)) {
        setEmployees(response.data);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const handleComputePayslip = async () => {
    if (!selectedEmployee) {
      setError("Please select an employee");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setPayslipData(null);
      setCalculation(null);

      const payload = {
        empId: selectedEmployee._id,
        fromDate: fromDate.format("YYYY-MM-DD"),
        toDate: toDate.format("YYYY-MM-DD"),
        accountDate: accountDate.format("YYYY-MM-DD"),
      };

      // Add manual work days if enabled
      if (useManualWorkDays && manualWorkDays) {
        payload.workDays = {
          total: parseInt(manualWorkDays.total) || 30,
          paid: parseInt(manualWorkDays.paid) || 30,
          unpaid: parseInt(manualWorkDays.unpaid) || 0,
        };
      }

      const response = await axios.post("/payroll/computePayslip", payload);

      if (response.data) {
        setPayslipData(response.data.payslip);
        setCalculation(response.data.calculation);
        setSuccess("Payslip computed successfully!");
        setShowCalculationDetails(true);
      }
    } catch (err) {
      setError(err.response?.data || "Error computing payslip");
      console.error("Error computing payslip:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return dayjs(date).format("DD MMM YYYY");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Compute Individual Payslip
        </Typography>

        {/* Employee Selection & Date Range */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payslip Details
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) =>
                    `${option.emp_id} - ${option.staff_name} (${option.department?.name || "N/A"})`
                  }
                  value={selectedEmployee}
                  onChange={(event, newValue) => setSelectedEmployee(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Employee" required fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Account Date"
                  value={accountDate}
                  onChange={(newValue) => setAccountDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              {/* Manual Work Days Override */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Work Days (Leave empty for automatic calculation from attendance)
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Total Days"
                  type="number"
                  value={manualWorkDays?.total || ""}
                  onChange={(e) => {
                    setUseManualWorkDays(true);
                    setManualWorkDays({
                      ...manualWorkDays,
                      total: e.target.value,
                    });
                  }}
                  fullWidth
                  placeholder="Auto-calculated"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Paid Days"
                  type="number"
                  value={manualWorkDays?.paid || ""}
                  onChange={(e) => {
                    setUseManualWorkDays(true);
                    setManualWorkDays({
                      ...manualWorkDays,
                      paid: e.target.value,
                    });
                  }}
                  fullWidth
                  placeholder="Auto-calculated"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Unpaid Days"
                  type="number"
                  value={manualWorkDays?.unpaid || ""}
                  onChange={(e) => {
                    setUseManualWorkDays(true);
                    setManualWorkDays({
                      ...manualWorkDays,
                      unpaid: e.target.value,
                    });
                  }}
                  fullWidth
                  placeholder="Auto-calculated"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleComputePayslip}
                  disabled={loading || !selectedEmployee}
                  startIcon={loading ? <CircularProgress size={20} /> : <CalculateIcon />}
                  size="large"
                >
                  {loading ? "Computing..." : "Compute Payslip"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payslip Result */}
        {payslipData && calculation && (
          <>
            {/* Summary Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Payslip Number
                    </Typography>
                    <Typography variant="h6">{payslipData.name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Period
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(payslipData.fromDate)} - {formatDate(payslipData.toDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Work Days
                    </Typography>
                    <Typography variant="body1">
                      {payslipData.workDays.paid} / {payslipData.workDays.total} days
                      {payslipData.workDays.unpaid > 0 && (
                        <Chip
                          label={`${payslipData.workDays.unpaid} unpaid`}
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip label={payslipData.status} color="primary" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Salary Breakdown */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Salary Breakdown</Typography>
                  <IconButton
                    onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                  >
                    {showCalculationDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={showCalculationDetails}>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Component</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Earnings */}
                        {payslipData.salaryCalc
                          .filter((item) => item.isEarning)
                          .map((item, index) => (
                            <TableRow key={`earning-${index}`}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>
                                <Chip label={item.type} size="small" color="success" />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.amount)}
                              </TableCell>
                            </TableRow>
                          ))}

                        {/* Gross Salary */}
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography fontWeight="bold">Gross Salary</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {formatCurrency(payslipData.grossSalary)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            <Divider />
                          </TableCell>
                        </TableRow>

                        {/* Deductions */}
                        {payslipData.salaryCalc
                          .filter((item) => !item.isEarning)
                          .map((item, index) => (
                            <TableRow key={`deduction-${index}`}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>
                                <Chip label={item.type} size="small" color="error" />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.amount)}
                              </TableCell>
                            </TableRow>
                          ))}

                        {/* Total Deductions */}
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography fontWeight="bold">Total Deductions</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="error.main">
                              {formatCurrency(payslipData.totalDeductions)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            <Divider />
                          </TableCell>
                        </TableRow>

                        {/* Net Salary */}
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="h6">Net Salary</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary">
                              {formatCurrency(payslipData.netSalary)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>

                {/* Quick Summary */}
                {!showCalculationDetails && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Gross Salary
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(payslipData.grossSalary)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Deductions
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {formatCurrency(payslipData.totalDeductions)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Net Salary
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(payslipData.netSalary)}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={() => alert("Save functionality to be implemented")}
              >
                Save Payslip
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => alert("Print functionality to be implemented")}
              >
                Print Payslip
              </Button>
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ComputePayslip;
